import React, { createContext, useContext, type ComponentProps } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../_placeholders';
import { Tag } from '../../tags/Tag';
import { ChevronUp, ChevronDown } from '@core/icons-react';
import { cn } from '../../../utils/classnames';
import { Shimmer } from '../Shimmer';

type PlanContextValue = {
  isStreaming: boolean;
};

const PlanContext = createContext<PlanContextValue | null>(null);

const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('Plan components must be used within Plan');
  }
  return context;
};

export type PlanProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
};

export const Plan = ({
  className,
  isStreaming = false,
  children,
  ...props
}: PlanProps) => (
  <PlanContext.Provider value={{ isStreaming }}>
    <Collapsible className={cn('core-plan', className)} {...props}>
      {children}
    </Collapsible>
  </PlanContext.Provider>
);

export type PlanHeaderProps = ComponentProps<'div'>;

export const PlanHeader = ({ className, ...props }: PlanHeaderProps) => (
  <div
    className={cn('core-plan-header', className)}
    {...props}
  />
);

export type PlanTitleProps = Omit<ComponentProps<'h3'>, 'children'> & {
  children: string;
};

export const PlanTitle = ({ className, children, ...props }: PlanTitleProps) => {
  const { isStreaming } = usePlan();

  return (
    <h3 className={cn('core-plan-title', className)} {...props}>
      {isStreaming ? <Shimmer>{children}</Shimmer> : children}
    </h3>
  );
};

export type PlanDescriptionProps = Omit<ComponentProps<'p'>, 'children'> & {
  children: string;
};

export const PlanDescription = ({
  className,
  children,
  ...props
}: PlanDescriptionProps) => {
  const { isStreaming } = usePlan();

  return (
    <p
      className={cn('core-plan-description', className)}
      {...props}
    >
      {isStreaming ? <Shimmer>{children}</Shimmer> : children}
    </p>
  );
};

export type PlanActionProps = ComponentProps<'div'>;

export const PlanAction = ({ className, ...props }: PlanActionProps) => (
  <div className={cn('core-plan-action', className)} {...props} />
);

export type PlanContentProps = ComponentProps<typeof CollapsibleContent>;

export const PlanContent = ({ className, ...props }: PlanContentProps) => (
  <CollapsibleContent asChild>
    <div className={cn('core-plan-content', className)} {...props} />
  </CollapsibleContent>
);

export type PlanFooterProps = ComponentProps<'div'>;

export const PlanFooter = ({ className, ...props }: PlanFooterProps) => (
  <div className={cn('core-plan-footer', className)} {...props} />
);

export type PlanTriggerProps = ComponentProps<typeof CollapsibleTrigger>;

export const PlanTrigger = ({ className, children, color: _color, ...props }: PlanTriggerProps) => (
  <CollapsibleTrigger asChild>
    <Tag
      template="action-sm"
      title="Toggle plan"
      className={cn('core-plan-trigger', className)}
      {...props}
    >
      {children ?? (
        <>
          <ChevronUp size={16} />
          <ChevronDown size={16} />
        </>
      )}
    </Tag>
  </CollapsibleTrigger>
);

Plan.displayName = 'Plan';
PlanHeader.displayName = 'PlanHeader';
PlanTitle.displayName = 'PlanTitle';
PlanDescription.displayName = 'PlanDescription';
PlanAction.displayName = 'PlanAction';
PlanContent.displayName = 'PlanContent';
PlanFooter.displayName = 'PlanFooter';
PlanTrigger.displayName = 'PlanTrigger';





